import React, { ReactNode } from 'react'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import Breadcrumbs from 'shared/components/breadcrumbs'
import { PrimaryButton } from 'shared/components/button'
import { UserRolesEnum } from 'shared/enums/user-roles-enum'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { SaveIconAlt } from 'shared/icons/save-icon'
import { isCustomDomain } from 'shared/utils/is-custom-domain'
import { shouldShowChangePasswordPage } from 'shared/utils/show-change-password-page'
import { getDashboardNotificationSettingsLink } from 'modules/header/menu/utils'
import ProfileMenuSkeleton from './profile-menu-skeleton'

function ProfileLayoutItem({
  show = true,
  href,
  label,
  ...options
}: React.LiHTMLAttributes<HTMLLIElement> & {
  show?: boolean
  href: string
  label: string
}): ReactNode {
  if (!show) return null

  return (
    <li {...options}>
      <Link
        href={href}
        data-active={globalThis.location.pathname === href}
        className="flex border-b border-b-text-secondary/10 p-5 text-text-primary hover:bg-brand-primary-subtle focus:outline-hidden focus-visible:border-b-brand-primary focus-visible:bg-brand-primary-subtle data-[active=true]:border-l-4 data-[active=true]:border-l-brand-primary data-[active=true]:bg-brand-primary-subtle data-[active=true]:font-bold data-[active=true]:text-brand-primary"
      >
        {label}
      </Link>
    </li>
  )
}

function ProfileLayoutContent(): ReactNode {
  const { t } = useTranslation()
  const { user, loading: isLoading } = useUser()

  if (isLoading) {
    return <ProfileMenuSkeleton />
  }

  if (!isAuthorizedUser(user)) return null

  const hasCustomerRole = user.roles.includes(UserRolesEnum.Customer)
  const showChangePasswordPage = shouldShowChangePasswordPage(user)

  return (
    <>
      <ProfileLayoutItem
        show={!user.workspaceOwner}
        key="user-settings"
        href="/profile/user-settings"
        label={t('profile.user_settings.profile.title')}
      />
      <ProfileLayoutItem
        show={!hasCustomerRole && !user.impersonator}
        key="account-settings"
        href="/profile/account-settings"
        label={t('profile.user_settings.account.title')}
      />
      <ProfileLayoutItem
        show={!user.workspaceOwner}
        key="settings-subscriptions"
        href="/profile/manage-subscriptions"
        label={t('profile.user_settings.manage_subscriptions.title')}
      />
      <ProfileLayoutItem
        key="notification-settings"
        href={
          hasCustomerRole && !isCustomDomain
            ? getDashboardNotificationSettingsLink(user)
            : '/profile/notification-settings'
        }
        label={t('profile.user_settings.manage_notifications.title')}
      />
      <ProfileLayoutItem
        show={showChangePasswordPage}
        key="change-passwords"
        href="/profile/change-password"
        label={t('profile.user_settings.change_password.title')}
      />
    </>
  )
}

export function ProfileLayout({
  isFetching,
  onSave,
  disabled,
  title,
  children,
}: {
  isFetching?: boolean
  onSave?: () => void
  disabled?: boolean
  title: string
  children: React.ReactNode
}): ReactNode {
  const { t } = useTranslation()
  const { user } = useUser()

  if (!user || !isAuthorizedUser(user)) {
    return <div />
  }

  const breadCrumbsData = [{ caption: t('user_settings.title') }, { caption: title }]

  return (
    <>
      <div className="mb-5 flex h-10 flex-wrap justify-between gap-x-3">
        <Breadcrumbs data={breadCrumbsData} isFetching={false} />
        <div className="ml-auto flex gap-3 sm:ml-0">
          {onSave && (
            <PrimaryButton
              data-testid="save-button"
              isLoading={isFetching}
              disabled={!user || disabled}
              onClick={onSave}
              className="flex items-center justify-between"
            >
              <SaveIconAlt />
              <span className="hidden font-bold sm:flex">{t('global.save')}</span>
            </PrimaryButton>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-3 md:gap-5 lg:flex-row lg:gap-12">
        <div className="relative min-h-48.75 w-full lg:w-62.5">
          <ul className="static inset-0 flex h-fit w-full flex-col overflow-auto rounded-md bg-background-secondary text-base last:border-b-transparent lg:w-62.5">
            <ProfileLayoutContent />
          </ul>
        </div>
        {children}
      </div>
    </>
  )
}
